import React, { useEffect, useMemo, useState } from 'react';
import { Clock, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

// Future-relative formatter (companion to formatRelativeTime which is
// past-only). Returns "in 5 min", "in 3 hours", "in 2 days" — used by
// the digest to show when a snoozed suggestion will come back.
function formatUntilRelative(ts, now = Date.now()) {
  const diffMs = Math.max(0, Number(ts) - now);
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'in less than a minute';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `in ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `in ${diffHr} hour${diffHr === 1 ? '' : 's'}`;
  const diffDay = Math.floor(diffHr / 24);
  return `in ${diffDay} day${diffDay === 1 ? '' : 's'}`;
}

/**
 * Snooze Digest — a compact settings section that lists every active
 * banner snooze so users can un-hide them on demand instead of waiting
 * for the timestamp to expire.
 *
 * Sources of truth (localStorage keys):
 *   • `dupe_banner_snoozed_until`         — global duplicate-banner snooze
 *   • `ai_tips_all_snoozed_until`         — global AI-suggestion snooze
 *   • `ai_tip_snoozed_until_{Family}`     — per-family AI-tip snoozes
 *
 * Renders nothing when no snoozes are active, so the section is invisible
 * when it has nothing useful to say.
 */
export const SnoozeDigest = () => {
  const { t } = useLanguage();
  // Bump this to re-read localStorage. Every "un-hide" tap increments it
  // so the row disappears immediately without re-mounting the modal.
  const [version, setVersion] = useState(0);

  // Also re-read whenever localStorage changes in another tab / window —
  // e.g. user hits "Hide for 30 days" on the library banner while the
  // settings modal is open in another tab.
  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key) return setVersion((v) => v + 1);
      if (
        e.key === 'dupe_banner_snoozed_until' ||
        e.key === 'ai_tips_all_snoozed_until' ||
        e.key.startsWith('ai_tip_snoozed_until_')
      ) setVersion((v) => v + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const active = useMemo(() => {
    const now = Date.now();
    const entries = [];
    const push = (key, label, testid) => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const ts = Number(raw);
        if (!Number.isFinite(ts) || ts <= now) return;
        entries.push({ key, label, until: ts, testid });
      } catch { /* privacy mode / quota */ }
    };
    push('dupe_banner_snoozed_until', t('snoozeDigestDupeLabel') || 'Duplicate suggestions', 'snooze-digest-row-dupe');
    push('ai_tips_all_snoozed_until', t('snoozeDigestAllAiLabel') || 'All AI suggestions', 'snooze-digest-row-ai-all');
    try {
      for (const key of Object.keys(localStorage)) {
        if (!key.startsWith('ai_tip_snoozed_until_')) continue;
        const fam = key.slice('ai_tip_snoozed_until_'.length);
        if (!fam) continue;
        push(
          key,
          (t('snoozeDigestAiTipLabel') || 'AI tip · {family}').replace('{family}', fam),
          `snooze-digest-row-ai-${fam}`
        );
      }
    } catch { /* privacy mode */ }
    // Soonest-expiring first so users see what's coming back next.
    entries.sort((a, b) => a.until - b.until);
    return entries;
  }, [version, t]);

  if (active.length === 0) return null;

  const unhide = (key) => {
    try { localStorage.removeItem(key); } catch { /* privacy mode */ }
    setVersion((v) => v + 1);
    toast.success(t('snoozeDigestUnhideToast') || 'Suggestion re-enabled');
  };

  const unhideAll = () => {
    for (const entry of active) {
      try { localStorage.removeItem(entry.key); } catch { /* privacy mode */ }
    }
    setVersion((v) => v + 1);
    toast.success(
      (t('snoozeDigestUnhideAllToast') || 'Re-enabled {n} snoozed suggestions').replace('{n}', active.length)
    );
  };

  return (
    <section
      className="space-y-2 border-t pt-4"
      aria-labelledby="snooze-digest-heading"
      data-testid="snooze-digest-section"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3
            id="snooze-digest-heading"
            className="text-base font-medium"
            style={{ color: 'hsl(143, 28%, 25%)' }}
          >
            {t('snoozeDigestTitle') || 'Snoozed suggestions'}{' '}
            <span
              className="inline-flex items-center justify-center rounded-full text-xs font-semibold ml-1"
              style={{
                minWidth: 20,
                height: 20,
                padding: '0 6px',
                backgroundColor: 'hsl(143, 28%, 90%)',
                color: 'hsl(143, 28%, 25%)',
              }}
              data-testid="snooze-digest-count"
            >
              {active.length}
            </span>
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'hsl(125, 12%, 40%)' }}>
            {t('snoozeDigestBlurb') || 'Suggestions you\u2019ve temporarily hidden. Un-hide any of them below.'}
          </p>
        </div>
        {active.length > 1 && (
          <button
            type="button"
            onClick={unhideAll}
            className="text-xs font-medium px-3 py-1.5 rounded-full transition-colors flex-shrink-0"
            style={{
              backgroundColor: 'hsl(143, 28%, 25%)',
              color: 'white',
              border: '1px solid hsl(143, 28%, 25%)',
            }}
            data-testid="snooze-digest-unhide-all"
          >
            {t('snoozeDigestUnhideAll') || 'Un-hide all'}
          </button>
        )}
      </div>
      <ul className="rounded-lg overflow-hidden" style={{ border: '1px solid hsl(143, 28%, 88%)' }}>
        {active.map((entry, idx) => (
          <li
            key={entry.key}
            className="flex items-center gap-3 px-3 py-2"
            style={{
              backgroundColor: 'white',
              borderTop: idx > 0 ? '1px solid hsl(143, 28%, 93%)' : 'none',
            }}
            data-testid={entry.testid}
          >
            <Clock className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(143, 45%, 34%)' }} aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'hsl(143, 28%, 25%)' }}>
                {entry.label}
              </div>
              <div className="text-[11px]" style={{ color: 'hsl(125, 12%, 45%)' }}>
                {(t('snoozeDigestUntil') || 'Returns {when}').replace('{when}', formatUntilRelative(entry.until))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => unhide(entry.key)}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-colors flex-shrink-0"
              style={{
                backgroundColor: 'white',
                color: 'hsl(143, 28%, 25%)',
                border: '1px solid hsl(143, 28%, 70%)',
              }}
              aria-label={(t('snoozeDigestUnhide') || 'Un-hide') + ' — ' + entry.label}
              data-testid={`${entry.testid}-unhide`}
            >
              <RotateCcw className="w-3 h-3" aria-hidden="true" />
              {t('snoozeDigestUnhide') || 'Un-hide'}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SnoozeDigest;
